import { BarChart } from '@/components/Chart/BarChart';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableNum,
    TableRow,
} from '@/components/Table/Table';
import { transactionsExportUrl, type TransactionsReport } from '@/features/reports/api';
import { TRANSACTIONS_COPY } from '@/features/reports/copy';
import { formatPercent, formatPounds, formatPoundsCompact } from '@/features/reports/format';
import { KpiTile, ReportCard } from '@/features/reports/parts';
import { PERIOD_AXIS, type ReportPeriod } from '@/features/reports/periods';
import { ExportButton } from '@/features/reports/transactions/ExportButton';
import { exportFileName } from '@/features/reports/transactions/labels';
import { cn } from '@/util/utils';

const COPY = TRANSACTIONS_COPY.margin;

const dash = (value: number | null, format: (v: number) => string) =>
    value === null ? '–' : format(value);

/**
 * Transactions › Margin (ALS-86): orders, AOV and margin per period, with the refunds settled in
 * each period coming off it (Franka, 21 Oct 2026). A refund lowers the period it was settled in,
 * never the period the order was sold in, so a past period never changes.
 */
export function MarginView({
    report,
    period,
}: {
    report: TransactionsReport;
    period: ReportPeriod;
}) {
    const { kpis, series } = report.margin;

    return (
        <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <KpiTile label={COPY.kpis.orders} metric={kpis.orders} format={String} />
                <KpiTile
                    label={COPY.kpis.averageOrderValue}
                    metric={kpis.averageOrderValue}
                    format={formatPoundsCompact}
                />
                <KpiTile
                    label={COPY.kpis.marginAfterRefunds}
                    metric={kpis.marginAfterRefunds}
                    format={formatPoundsCompact}
                />
                <KpiTile
                    label={COPY.kpis.refunds}
                    metric={kpis.refunds}
                    format={formatPoundsCompact}
                    lowerIsBetter
                />
                <KpiTile
                    label={COPY.kpis.averageMargin}
                    metric={kpis.averageMargin}
                    format={formatPoundsCompact}
                />
                <KpiTile
                    label={COPY.kpis.marginPercentage}
                    metric={kpis.marginPercentage}
                    format={formatPercent}
                    unit="pt"
                />
            </div>

            <ReportCard
                title={COPY.chart.title}
                subtitle={COPY.chart.subtitle(PERIOD_AXIS[period])}
            >
                <BarChart
                    title={COPY.chart.title}
                    labels={series.map((b) => b.label)}
                    series={[
                        {
                            label: COPY.chart.before,
                            values: series.map((b) => b.marginBeforeRefunds),
                            colour: 0,
                        },
                        {
                            label: COPY.chart.refunds,
                            values: series.map((b) => b.refunds),
                            colour: 5,
                        },
                    ]}
                    topLabels={series.map((b) =>
                        b.marginPercentage === null ? null : formatPercent(b.marginPercentage),
                    )}
                    format={formatPoundsCompact}
                />
            </ReportCard>

            <ReportCard
                title={COPY.table.title}
                subtitle={COPY.table.subtitle}
                actions={
                    <ExportButton
                        url={transactionsExportUrl('Margin', period)}
                        fileName={exportFileName(['margin', period])}
                    />
                }
            >
                {/* Periods against nine measures is a matrix, so a phone scrolls it sideways in a
                    labelled region rather than stacking it into cards nobody can compare. */}
                <Table region={COPY.table.title}>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{COPY.table.period}</TableHead>
                            <TableHead className="text-right">{COPY.table.orders}</TableHead>
                            <TableHead className="text-right">{COPY.table.revenue}</TableHead>
                            <TableHead className="text-right">
                                {COPY.table.averageOrderValue}
                            </TableHead>
                            <TableHead className="text-right">{COPY.table.before}</TableHead>
                            <TableHead className="text-right">{COPY.table.refunds}</TableHead>
                            <TableHead className="text-right">{COPY.table.after}</TableHead>
                            <TableHead className="text-right">{COPY.table.averageMargin}</TableHead>
                            <TableHead className="text-right">{COPY.table.percentage}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {series.map((b) => (
                            <TableRow key={b.from}>
                                <TableCell className="font-medium whitespace-nowrap">
                                    {b.label}
                                </TableCell>
                                <TableNum>{b.orders}</TableNum>
                                <TableNum>{formatPounds(b.revenue)}</TableNum>
                                <TableNum>{dash(b.averageOrderValue, formatPounds)}</TableNum>
                                <TableNum>{formatPounds(b.marginBeforeRefunds)}</TableNum>
                                <TableNum>
                                    {b.refunds === 0
                                        ? formatPounds(0)
                                        : `-${formatPounds(b.refunds)}`}
                                </TableNum>
                                <TableNum
                                    className={cn(
                                        'font-medium',
                                        b.marginAfterRefunds < 0 && 'text-destructive',
                                    )}
                                >
                                    {formatPounds(b.marginAfterRefunds)}
                                </TableNum>
                                <TableNum>{dash(b.averageMargin, formatPounds)}</TableNum>
                                <TableNum>{dash(b.marginPercentage, formatPercent)}</TableNum>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </ReportCard>
        </>
    );
}
